<template>
    <ChatAgentToolFrame is-actionable :is-complete="isSubmitted" :completion-message="completionMessage">
        <div v-if="!isSubmitted" class="nadia-chat flex flex-col gap-1" data-testid="chat-push-manager-review-to-workday">
            <div class="flex items-center justify-between gap-2">
                <div class="nadia-chat text-valence-primary-500">{{ reviewTitle }} — Draft</div>
                <BaseButton theme="tertiary" data-testid="toggle-edit-manager-review" :disabled="loading" @click="toggleEdit">
                    <i class="bi" :class="editing ? 'bi-check-lg' : 'bi-pencil'" aria-hidden="true"></i>
                    {{ editing ? "Done" : "Edit" }}
                </BaseButton>
            </div>
            <div class="text-valence-grey-750">Drafted from your conversation. Review or edit your comments below, then submit them to Workday when you're ready.</div>
        </div>

        <div v-if="!isSubmitted" class="flex flex-col gap-5">
            <section v-for="section in draft.sections" :key="section.section_id" class="flex flex-col gap-2">
                <h4 class="body-semibold text-valence-grey-800">{{ section.label }}</h4>

                <div class="flex flex-col gap-1">
                    <div class="body-small text-valence-grey-600">Overall {{ section.label }} comment</div>
                    <BaseTextarea
                        v-if="editing"
                        v-model:input="section.comment"
                        rows="3"
                        :disabled="loading"
                        :aria-label="`Overall ${section.label} comment`"
                        :placeholder="`Add an overall comment for ${section.label}.`"
                        class="caret-valence-primary-500 leading-[normal]"
                        @blur="saveDraft" />
                    <p v-else class="body-small text-valence-grey-750 whitespace-pre-line">{{ section.comment || emptyPlaceholder }}</p>
                </div>

                <div v-for="item in section.items" :key="item.item_id" class="flex flex-col gap-1 pl-3 border-l-2 border-valence-grey-200">
                    <div class="body-small text-valence-grey-600">{{ item.label }}</div>
                    <BaseTextarea
                        v-if="editing"
                        v-model:input="item.comment"
                        rows="2"
                        :disabled="loading"
                        :aria-label="item.label"
                        :placeholder="`Add a comment on ${item.label}.`"
                        class="caret-valence-primary-500 leading-[normal]"
                        @blur="saveDraft" />
                    <p v-else class="body-small text-valence-grey-750 whitespace-pre-line">{{ item.comment || emptyPlaceholder }}</p>
                </div>
            </section>

            <section class="flex flex-col gap-1">
                <h4 class="body-semibold text-valence-grey-800">Overall review comment</h4>
                <BaseTextarea
                    v-if="editing"
                    v-model:input="draft.overall_comment"
                    rows="3"
                    :disabled="loading"
                    aria-label="Overall review comment"
                    placeholder="Add an overall comment for the review."
                    class="caret-valence-primary-500 leading-[normal]"
                    @blur="saveDraft" />
                <p v-else class="body-small text-valence-grey-750 whitespace-pre-line">{{ draft.overall_comment || emptyPlaceholder }}</p>
            </section>
        </div>

        <div v-if="!isSubmitted && (saving || hasUnsavedEdits)" class="body-small text-valence-grey-600" aria-live="polite">
            {{ saving ? "Saving your edits…" : "Edited — not yet submitted" }}
        </div>
        <div v-if="noticeMessage" class="body-small text-red-700" role="alert">{{ noticeMessage }}</div>

        <!-- Not disabled during draft saves: handleSubmit awaits the pending save, so a mid-save
             click is safe and never races the version counter. -->
        <BaseButton
            v-if="!isSubmitted"
            :disabled="loading"
            class="flex justify-center items-center gap-1"
            theme="primary"
            data-testid="submit-manager-review"
            @click="handleSubmit">
            <LoadingSpinner v-if="loading" class="h-4" />Submit to Workday
        </BaseButton>
    </ChatAgentToolFrame>
</template>

<script setup lang="ts">
import { ChatAction } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { useActionHandler } from "~vue/composables/useActionHandler";
import { useDeferredLoading } from "~vue/composables/useDeferredLoading";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "~vue/Moments/constants";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, reactive, ref } from "vue";

import ChatAgentToolFrame from "./ChatAgentToolFrame.vue";

interface CardItem {
    item_id: string;
    label: string;
    comment: string;
}
interface CardSection {
    section_id: string;
    label: string;
    comment: string;
    items: CardItem[];
}
interface CardDraft {
    review_ref: string;
    overall_comment: string;
    sections: CardSection[];
}

interface ActionResult {
    success?: boolean;
    payload?: {
        error?: string | null;
        draft_version?: number;
        draft?: CardDraft;
    };
}

interface Props {
    line: {
        action_params?: {
            draft?: CardDraft;
            reviewee_name?: string | null;
            integration_name?: string | null;
            draft_version?: number;
        };
        action_state?: {
            submitted?: boolean;
        } | null;
    };
    lineIdx: number;
    messageId: number;
}

const props = defineProps<Props>();

const { execute } = useActionHandler();
const updateActionState = inject<(payload: object) => void>("updateActionState")!;
const coachingSessionUuid = inject<string>("coachingSessionUuid")!;
const { loading, start: startLoading, stop: stopLoading } = useDeferredLoading();

const emptyPlaceholder = "No comment yet.";
const editing = ref(false);
const saving = ref(false);
const noticeMessage = ref("");

const isSubmitted = computed(() => Boolean(props.line.action_state?.submitted));
const revieweeName = computed(() => props.line.action_params?.reviewee_name || "your report");
const reviewTitle = computed(() => `${revieweeName.value}'s review`);
const completionMessage = computed(() => `Your comments have been submitted to ${revieweeName.value}'s Workday review.`);

function cloneDraft(): CardDraft {
    const source = props.line.action_params?.draft;
    const base: CardDraft = source ? JSON.parse(JSON.stringify(source)) : { review_ref: "", overall_comment: "", sections: [] };
    // Normalize so every editable field is a string bound to a textarea.
    base.overall_comment = base.overall_comment ?? "";
    base.sections = (base.sections ?? []).map((s) => ({
        ...s,
        comment: s.comment ?? "",
        items: (s.items ?? []).map((i) => ({ ...i, comment: i.comment ?? "" })),
    }));
    return base;
}

const draft = reactive<CardDraft>(cloneDraft());
const draftVersion = ref(props.line.action_params?.draft_version ?? 0);

// The comment-only projection sent to the backend and used for dirty comparison. Identifiers are
// server-owned; only the comment text is client-authored.
function commentPayload() {
    return {
        overall_comment: draft.overall_comment,
        sections: draft.sections.map((section) => ({
            section_id: section.section_id,
            comment: section.comment,
            items: section.items.map((item) => ({ item_id: item.item_id, comment: item.comment })),
        })),
    };
}

const savedSnapshot = ref(JSON.stringify(commentPayload()));
const hasUnsavedEdits = computed(() => JSON.stringify(commentPayload()) !== savedSnapshot.value);

// Serializes blur-saves and lets submit wait for an in-flight save, so a save's version bump can
// never race the submit's base_version.
let pendingSave: Promise<void> = Promise.resolve();

function adoptServerDraft(payload: { draft_version?: number; draft?: CardDraft }) {
    if (payload.draft) {
        draft.overall_comment = payload.draft.overall_comment ?? "";
        const incoming = new Map((payload.draft.sections ?? []).map((s) => [s.section_id, s]));
        for (const section of draft.sections) {
            const serverSection = incoming.get(section.section_id);
            if (!serverSection) {
                continue;
            }
            section.comment = serverSection.comment ?? "";
            const serverItems = new Map((serverSection.items ?? []).map((i) => [i.item_id, i]));
            for (const item of section.items) {
                item.comment = serverItems.get(item.item_id)?.comment ?? item.comment;
            }
        }
    }
    if (payload.draft_version !== undefined) {
        draftVersion.value = payload.draft_version;
    }
    savedSnapshot.value = JSON.stringify(commentPayload());
    updateActionState({
        message_id: props.messageId,
        lineIdx: props.lineIdx,
        action_params: { draft: JSON.parse(JSON.stringify(draft)), draft_version: draftVersion.value },
    });
}

function toggleEdit() {
    editing.value = !editing.value;
    if (!editing.value) {
        saveDraft();
    } else {
        logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}edit_manager_review_draft`, { session_id: coachingSessionUuid }, coachingSessionUuid);
    }
}

function saveDraft(): Promise<void> {
    if (!hasUnsavedEdits.value) {
        return pendingSave;
    }
    pendingSave = pendingSave.then(doSave);
    return pendingSave;
}

async function doSave() {
    if (!hasUnsavedEdits.value) {
        return;
    }
    saving.value = true;
    noticeMessage.value = "";
    try {
        const result: ActionResult = await execute(
            ChatAction.PUSH_MANAGER_REVIEW_TO_WORKDAY,
            {
                message_id: props.messageId,
                lineIdx: props.lineIdx,
                subaction: "save_draft",
                base_version: draftVersion.value,
                action_params: commentPayload(),
            },
            { sync: true },
        );
        if (result?.success) {
            draftVersion.value = result.payload?.draft_version ?? draftVersion.value;
            savedSnapshot.value = JSON.stringify(commentPayload());
        } else {
            noticeMessage.value = result?.payload?.error ?? "We couldn't save your edits. Please try again.";
            if (result?.payload?.draft) {
                adoptServerDraft(result.payload);
            }
        }
    } catch (e) {
        logError(e);
        noticeMessage.value = "We couldn't save your edits. Please try again.";
    } finally {
        saving.value = false;
    }
}

async function handleSubmit() {
    startLoading();
    noticeMessage.value = "";
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}push_manager_review_clicked`, { session_id: coachingSessionUuid }, coachingSessionUuid);

    try {
        await pendingSave;
        const result: ActionResult = await execute(
            ChatAction.PUSH_MANAGER_REVIEW_TO_WORKDAY,
            {
                message_id: props.messageId,
                lineIdx: props.lineIdx,
                base_version: draftVersion.value,
                action_params: commentPayload(),
            },
            { sync: true },
        );
        if (result?.success) {
            savedSnapshot.value = JSON.stringify(commentPayload());
            updateActionState({ message_id: props.messageId, lineIdx: props.lineIdx, action_state: { submitted: true } });
        } else {
            noticeMessage.value = result?.payload?.error ?? "We couldn't submit this review to Workday. Please try again later.";
            if (result?.payload?.draft) {
                adoptServerDraft(result.payload);
            }
        }
    } catch (e) {
        logError(e);
        noticeMessage.value = "We couldn't submit this review to Workday. Please try again later.";
    } finally {
        stopLoading();
    }
}
</script>
